/**
 * №16.1 Stage C — pure helpers + types shared by the author-canvas components
 * (no JSX, so the fast-refresh boundary stays clean — the wf-fullview-shared.ts
 * convention). Trigger read-back, outcome colour slots, verb glyphs, and the
 * plain-English read-backs for effects + conditions (the canvas folds config to
 * English; typed builders live in the inspector).
 */
import type { SegmentView, StepView, EffectView, ConditionLeaf, TemplateDetail } from "../../hooks/use-workflow-templates";

// The 5-slot branch-identity palette (colour budget: paths never impersonate a
// state colour; grey is reserved for not-taken/disabled). Console token ramps.
export const OUTCOME_COLORS = [
  "var(--c-purple-600)",
  "var(--c-teal-600)",
  "var(--c-amber-600)",
  "var(--c-blue-600)",
  "var(--c-coral-600)",
];
export const OUTCOME_WASHES = [
  "var(--c-purple-50)",
  "var(--c-teal-50)",
  "var(--c-amber-50)",
  "var(--c-blue-50)",
  "var(--c-coral-50)",
];
export function outcomeColor(i: number): string { return OUTCOME_COLORS[((i % OUTCOME_COLORS.length) + OUTCOME_COLORS.length) % OUTCOME_COLORS.length]!; }
export function outcomeWash(i: number): string { return OUTCOME_WASHES[((i % OUTCOME_WASHES.length) + OUTCOME_WASHES.length) % OUTCOME_WASHES.length]!; }

// One glyph per action-kind (the canvas verb glyphs — ● human · ◷ wait · ✦ AI ·
// ⚙ native · ▤ document).
export function verbGlyph(action: string): string {
  switch (action) {
    case "wait": return "◷";
    case "aiStep": return "✦";
    case "nativeOp": case "nativeMatterOp": return "⚙";
    case "contraGenerate": case "generateDocument": return "▤";
    case "interview": return "☰";
    default: return "●"; // humanTask + unregistered → a plain task
  }
}

const nameOfSegment = (segments: SegmentView[], no: number): string => segments.find((s) => s.segmentNo === no)?.name ?? `segment ${no}`;

/** The "opens when" read-back — identical grammar for a segment and its decision. */
export function triggerLabel(seg: SegmentView, segments: SegmentView[]): string {
  switch (seg.triggerKind) {
    case "withMatter": return "with the matter";
    case "afterSegments": {
      if (seg.dependsOn.length === 0) return "⚠ waits on nothing";
      const names = seg.dependsOn.map((d) => nameOfSegment(segments, d));
      const shown = names.slice(0, 2).join(seg.joinMode === "any" ? " or " : " + ");
      return `after ${shown}${names.length > 2 ? " +…" : ""}`;
    }
    case "afterDecision": {
      const dec = segments.find((s) => s.segmentNo === seg.triggerSegmentNo);
      const q = dec?.decisionName ?? `segment ${seg.triggerSegmentNo}`;
      return `when ${q} → ${seg.triggerOutcomeCode ?? "?"}`;
    }
    case "date": return seg.triggerDateType ? `on a date · ${seg.triggerDateType}` : "on a date (standing)";
    case "gateResolves": return "when the prior gate resolves";
    case "stepsComplete": return "when earlier work converges";
    default: return seg.triggerKind;
  }
}

/** A segment that opens with the matter (part of the START concurrency band). */
export const isStartSegment = (seg: SegmentView): boolean => seg.triggerKind === "withMatter";

/** A date-triggered segment with no causal anchor sits in the standing lane. */
export const isStandingSegment = (seg: SegmentView): boolean => seg.triggerKind === "date";

/** Plain-English read-back of a WRITE-only effect (r12). */
export function effectLabel(e: EffectView): string {
  const p = (e.params ?? {}) as Record<string, unknown>;
  switch (e.verb) {
    case "setMatterDate": return `set date ${String(p.dateType ?? "?")}${p.date ? ` = ${String(p.date)}` : " = decision date"}`;
    case "setAttribute": return `set ${String(p.attribute ?? "attribute")} = "${String(p.value ?? "")}"`;
    case "setMatterField": return `set ${String(p.field ?? "field")} = "${String(p.value ?? "")}"`;
    case "assign": return `assign ${String(p.role ?? p.target ?? "role")}${p.person ? ` → ${String(p.person)}` : ""}`;
    default: return `${e.verb}(…)`;
  }
}
export function effectWhen(e: EffectView): string {
  if (e.ownerKind === "outcome") return `on outcome ${e.outcomeCode ?? ""}`.trim();
  if (e.ownerKind === "decision") return "on decide";
  if (e.ownerKind === "segment") return e.event === "onOpen" ? "on segment open" : "on segment done";
  if (e.ownerKind === "step") return "on step done";
  if (e.ownerKind === "workflow") return e.event === "onStart" ? "on start" : "on complete";
  return e.event;
}

const OP_WORDS: Record<string, string> = { eq: "is", neq: "is not", isSet: "is set", isNotSet: "is not set", gt: ">", gte: "≥", lt: "<", lte: "≤" };
/** Plain-English read-back of an applicability leaf (r14). */
export function conditionLabel(c: ConditionLeaf): string {
  const op = OP_WORDS[c.op] ?? c.op;
  if (c.op === "isSet" || c.op === "isNotSet") return `${c.field} ${op}`;
  return `${c.field} ${op} ${c.value ?? ""}`.trim();
}

/** Assignee read-back — never a raw code alone (W65 spirit); role vs person vs agent. */
export function assigneeLabel(step: StepView): string {
  if (!step.assigneeCode) return step.assigneeKind === "role" ? "unassigned role" : "—";
  const prefix = step.assigneeKind === "role" ? "↳ " : "";
  return `${prefix}${step.assigneeCode}`;
}

// The trigger-kind options served in the unified "Opens when" panel (the visible
// set; the engine also has gateResolves/stepsComplete as read-compat convergence).
export const TRIGGER_KINDS: { value: string; label: string; segmentOnly?: boolean }[] = [
  { value: "withMatter", label: "with the matter", segmentOnly: true },
  { value: "afterSegments", label: "after other segments complete" },
  { value: "afterDecision", label: "when a decision resolves" },
  { value: "date", label: "on a date" },
];

/** Does this template's validation carry a blocking error? (publish gate) */
export const isPublishable = (d: TemplateDetail | undefined): boolean => !!d?.validation?.valid;

// ── the render tree (№16.1.3 — causal + banded, the BC projection shape) ────
// The flat segment list nests into a spine that reads like BC's: ordered by
// CAUSALITY (dependency depth), NOT by displayOrder — so a segment always renders
// after the work it opens after, wherever it was authored (fixes the "renders at
// the bottom" bug). Segments that share the same START CRITERIA (same trigger +
// same dependency set) are CONCURRENT and render together as a BAND — anywhere on
// the canvas, trunk or branch, not just a hardcoded start group. A band of ONE is
// drawn plain (a lone segment is not a concurrency group, BC r4.1 §1). Each
// decision carries its outcome branches; convergence (afterSegments over >1 dep)
// returns to the trunk (pull-not-push — an outcome never names a successor).
export interface SpineSegment { kind: "segment"; seg: SegmentView; }
export interface SpineBranch { code: string; label: string; isDefault: boolean; nodes: SpineNode[]; }
export interface SpineDecision { kind: "decision"; seg: SegmentView; branches: SpineBranch[]; }
/** A concurrency band — 2+ segments that open at the same point. Band-of-one is never emitted. */
export interface SpineBand { kind: "band"; startKey: string; members: Array<SpineSegment | SpineDecision>; }
export type SpineNode = SpineSegment | SpineDecision | SpineBand;

/** The concurrency key: segments sharing it open at the same point → they band together. */
export function startKey(seg: SegmentView): string {
  if (seg.triggerKind === "afterDecision") return `ad:${seg.triggerSegmentNo}:${seg.triggerOutcomeCode ?? ""}`;
  if (seg.triggerKind === "afterSegments" && seg.dependsOn.length > 0) return `as:${[...seg.dependsOn].sort((a, b) => a - b).join(",")}`;
  if (seg.triggerKind === "date") return `date:${seg.segmentNo}`; // standing lane — filtered before the spine
  return "wm"; // withMatter, or a dep-less afterSegments (engine-equivalent to withMatter)
}

export function buildSpine(segments: SegmentView[]): SpineNode[] {
  const bySeg = new Map(segments.map((s) => [s.segmentNo, s]));

  // branchKey: afterDecision lineage nests in its branch zone; a lone-dep afterSegments
  // continuation stays on the same branch; withMatter + convergence return to the trunk (null).
  const branchKey = new Map<number, string | null>();
  const compute = (seg: SegmentView, seen: Set<number>): string | null => {
    if (branchKey.has(seg.segmentNo)) return branchKey.get(seg.segmentNo)!;
    if (seen.has(seg.segmentNo)) return null;
    seen.add(seg.segmentNo);
    let key: string | null = null;
    if (seg.triggerKind === "afterDecision" && seg.triggerOutcomeCode) key = `${seg.triggerSegmentNo}:${seg.triggerOutcomeCode}`;
    else if (seg.triggerKind === "afterSegments" && seg.dependsOn.length === 1) {
      const dep = bySeg.get(seg.dependsOn[0]!);
      key = dep ? compute(dep, seen) : null;
    }
    branchKey.set(seg.segmentNo, key);
    return key;
  };
  segments.forEach((s) => compute(s, new Set()));

  // Causal depth = longest chain of afterSegments dependencies. withMatter / branch head = 0.
  const depthMemo = new Map<number, number>();
  const depth = (seg: SegmentView, seen: Set<number>): number => {
    if (depthMemo.has(seg.segmentNo)) return depthMemo.get(seg.segmentNo)!;
    if (seen.has(seg.segmentNo)) return 0;
    seen.add(seg.segmentNo);
    let d = 0;
    if (seg.triggerKind === "afterSegments" && seg.dependsOn.length > 0) {
      d = 1 + Math.max(0, ...seg.dependsOn.map((x) => { const dep = bySeg.get(x); return dep ? depth(dep, seen) : 0; }));
    }
    depthMemo.set(seg.segmentNo, d);
    return d;
  };

  const nodeFor = (seg: SegmentView): SpineSegment | SpineDecision => seg.hasDecision
    ? { kind: "decision", seg, branches: seg.outcomes.map((o) => ({ code: o.code, label: o.label, isDefault: o.isDefault, nodes: levelize(`${seg.segmentNo}:${o.code}`) })) }
    : { kind: "segment", seg };

  const levelize = (key: string | null): SpineNode[] => {
    const members = segments.filter((s) => (branchKey.get(s.segmentNo) ?? null) === key && s.triggerKind !== "date");
    // Partition into concurrency cells (same start criteria = same open-point).
    const cells = new Map<string, SegmentView[]>();
    for (const s of members) { const k = startKey(s); (cells.get(k) ?? cells.set(k, []).get(k)!).push(s); }
    // Order cells by causal depth, then a stable displayOrder/segmentNo tiebreak among peers.
    const ordered = [...cells.entries()].sort(([, A], [, B]) => {
      const da = depth(A[0]!, new Set()), db = depth(B[0]!, new Set());
      if (da !== db) return da - db;
      const oa = Math.min(...A.map((s) => s.displayOrder)), ob = Math.min(...B.map((s) => s.displayOrder));
      if (oa !== ob) return oa - ob;
      return Math.min(...A.map((s) => s.segmentNo)) - Math.min(...B.map((s) => s.segmentNo));
    });
    return ordered.map(([k, cell]): SpineNode => {
      const sorted = cell.slice().sort((a, b) => a.displayOrder - b.displayOrder || a.segmentNo - b.segmentNo);
      return sorted.length === 1 ? nodeFor(sorted[0]!) : { kind: "band", startKey: k, members: sorted.map(nodeFor) };
    });
  };

  return levelize(null);
}

/** The tail segment(s) of a branch path — the last node's segment(s), a whole band if it ends in one
 *  (all members are concurrent, so a rejoin waits on all of them). A4 rejoin wiring, r8 §2.3. */
export function branchTail(nodes: SpineNode[]): number[] {
  const last = nodes[nodes.length - 1];
  if (!last) return [];
  if (last.kind === "band") return last.members.map((m) => m.seg.segmentNo);
  return [last.seg.segmentNo];
}

/** Findings scoped to a segment / step for inline surfacing. */
export function findingsFor(d: TemplateDetail | undefined, segmentNo?: number, stepNo?: number): { severity: string; code: string; message: string; segmentNo?: number; stepNo?: number }[] {
  if (!d?.validation) return [];
  return d.validation.findings.filter((f) => (segmentNo != null ? f.segmentNo === segmentNo : true) && (stepNo != null ? f.stepNo === stepNo : true));
}
